import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Flex, Text, Heading, Image, Button } from 'rebass';
import { useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import useSWR from 'swr';
import Loading from 'app/loading/index';
import useEntity from 'entities/hooks/use-entity';
import { useCelebrationClips, useTimelineClips } from './hooks/index';
import useTimerTo from './components/Trimmer/hooks/timer';
import PreviewControls from 'stories/tell-a-story/PreviewControls';
import PreviewNode from './components/PreviewNode';
import VideoNode from './components/VideoNode';
import BufferingOverlay from 'app/modal/BufferingOverlay';
import { VideoPreviewContextProvider } from './VideoPreviewContext';
import store from 'store';
import { get, getSWR, patch } from 'utils/api';
import Modal from 'app/modal/v3/index';
import BabyBunnyWClock from './assets/BabyBunnyWClock.png'
import Wrapper from './Wrapper';
import useEntities from 'entities/hooks/use-entities';

function RenderEmptyState() {
  return (
    <Flex
      width="100%"
      height={500}
      color="white"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      textAlign="center"
      px={2}
      sx={{
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Heading mt={3} mb={2} color="#0E0E0E" className="Poppins">
        Preview pane
      </Heading>
      <Text className="dm-sans" color="#0E0E0E" mb={3}>
        Here’s where your video preview will play after you’ve added clips.
      </Text>
    </Flex>
  );
}

const WatchMeGrowVideoPreview = () => {
  const params = useParams();
  const { entity } = useEntity(params);
  const firstBookId = entity?.books?.[0]?.id || '';
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [message, setMessage] = useState(null);
  const [monthsFrameLayersData, setMonthsFrameLayersData] = useState([]);

  useSWR(firstBookId ? `/v3/books/${firstBookId}/watch_me_grow_months` : null);
  const { data: bookData } = useSWR(firstBookId ? `/v3/books/${firstBookId}` : null);
  const videoId = bookData?.data?.data?.attributes?.['watch-me-grow-video-id'];
  const bookStyleMonth = bookData?.data?.included.find(bookDetail => {
    const onePagerDeets = bookData?.data?.data?.relationships?.['watch-me-grow-one-pager']?.data
    return bookDetail?.id === onePagerDeets?.id && bookDetail?.type === onePagerDeets?.type
  })

  const monthLayers = useEntities('watch-me-grow-months');

  const { data } = useSWR(firstBookId && videoId ? `/v3/books/${firstBookId}/watch_me_grow_videos/${videoId}` : null, getSWR, { refreshInterval });
  const processingState = data?.data?.data?.attributes?.['video-is-being-rendered'];
  const allowRendering = data?.data?.data?.attributes?.['allow-rendering-of-watch-me-grow-video'];
  const reasonNotRenderVideo = data?.data?.data?.attributes?.['reason-to-not-render-video'];
  const videoData = data?.data?.data
  const included = data?.data?.included

  const videoDetails = videoData?.relationships?.['video'].data;
  const video = included && included.find(i => i.id === videoDetails?.id && i.type === videoDetails?.type);

  const clips = useCelebrationClips(firstBookId)
  const timelineClips = useTimelineClips(clips.map((cli, index) => ({ ...cli, position: index })))

  const { width } = useWindowSize();

  const [shouldPlay, setShouldPlay] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [processVideo, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(true);

  async function fetchMonthLayersData() {
    const finalResult = await Promise.all(
      Object.keys(monthLayers).map(async (id) => {
        const { data: layersData } = await get(`/v3/books/${firstBookId}/watch_me_grow_months/${id}/watch_me_grow_month_frame_layers`);
        const response = layersData.initial;

        const includedImages = response.included.reduce((acc, image) => {
          acc[image.id] = image.attributes;
          return acc;
        }, {});

        return {
          [id]: response.data.reduce((result, layer) => {
            const layerId = layer.id;
            const photoId = layer.relationships.photo?.data?.id;
            const canvasImageId = layer.relationships['canvas-image']?.data?.id;

            result[layerId] = {
              photo: photoId ? includedImages[photoId] : null,
              canvasImage: canvasImageId ? includedImages[canvasImageId] : null,
              otherAttributes: layer.attributes,
            };

            return result;
          }, {})
        };
      })
    )
    setIsDataLoaded(false)
    setMonthsFrameLayersData(finalResult)
  }

  useEffect(() => {
    if (Object.keys(monthLayers).length > 0) {
      fetchMonthLayersData()
    }
  }, [monthLayers])

  useEffect(() => {
    if (processingState === true) {
      setIsLoading(true)
      setRefreshInterval(5000);
    }

    if (refreshInterval !== null && (processingState === false)) {
      setRefreshInterval(0);
      setIsLoading(false)
      setIsProcessing(false)
    }
  }, [processingState, setRefreshInterval]);

  const processWatchMeGrowVideo = async () => {
    setIsLoading(true)
    await patch(`/v3/books/${firstBookId}/watch_me_grow_videos/${videoId}/compose`);
    setRefreshInterval(5000)
  }

  const allClipsEndAt = useMemo(() => {
    if (!timelineClips.length) return 0;
    const [lastClip] = timelineClips.slice(-1);
    return lastClip.endAt;
  }, [timelineClips]);

  const [{ seconds, endSeconds, isRunning }, dispatch] = useTimerTo(allClipsEndAt);

  // ── One persistent, user-activated <video> for the whole sequence ───────────
  // Mobile Safari only lets a <video> play WITH SOUND if THAT element was activated by
  // a user tap. Mounting a fresh element per clip means later clips get blocked (and
  // unmuting a non-activated element re-blocks it). So we keep ONE element for all video
  // clips, activate it inside the Play tap, and swap its source as clips change — the
  // activation carries over, so every clip plays with sound.
  const currentClip = useMemo(
    () => timelineClips.find((c) => seconds >= c.startAt && seconds <= c.endAt) || null,
    [timelineClips, seconds]
  );
  const currentVideoClip = currentClip && currentClip.video ? currentClip : null;
  const firstVideoClip = useMemo(() => timelineClips.find((c) => c.video) || null, [timelineClips]);
  const lastVideoClipRef = useRef(null);
  if (currentVideoClip) lastVideoClipRef.current = currentVideoClip;
  // The clip whose source the persistent element currently holds: the in-range video,
  // else the last one it played, else the first video (so it's mounted from the start
  // and can be primed by the tap even when the preview opens on an image clip).
  const persistentVideoClip = currentVideoClip || lastVideoClipRef.current || firstVideoClip;

  const currentVideoClipRef = useRef(currentVideoClip);
  useEffect(() => { currentVideoClipRef.current = currentVideoClip; }, [currentVideoClip]);

  const getPersistentVideoEl = () => document.querySelector('[id="wmg-persistent-video"] video');

  const handlePlayClick = useCallback(() => {
    // Activate the persistent element INSIDE the tap so every clip can play with sound.
    // If a video is on-screen, start it; otherwise activate it silently for later clips.
    const v = getPersistentVideoEl();
    if (v) {
      try {
        if (currentVideoClipRef.current) {
          v.muted = false;
          const p = v.play();
          if (p && p.catch) p.catch(() => { });
        } else {
          v.muted = true;
          const p = v.play();
          if (p && p.then) {
            p.then(() => { try { v.pause(); v.muted = false; } catch (e) { /* noop */ } }).catch(() => { });
          }
        }
      } catch (e) { /* noop */ }
    }
    store.set('ClipPlaying', true);
    setShouldPlay(true);
  }, [setShouldPlay]);

  const handleProgress = useCallback(
    (value) => {
      if (value === 0 || value) dispatch({ type: 'setSeconds', seconds: value });
    },
    [dispatch]
  );

  const handlePauseClick = useCallback(() => {
    store.remove('ClipPlaying');
    setShouldPlay(false);
  }, [setShouldPlay]);

  const handleSliderChange = useCallback(
    (value) => {
      store.remove('ClipPlaying');
      dispatch({ type: 'stop' });
      dispatch({ type: 'setSeconds', seconds: value });
      store.set('currentSeconds', value);
      setShouldPlay(false);
    },
    [dispatch, isRunning]
  );

  const handleOnPlay = useCallback(() => {
    dispatch({ type: 'start' });
  }, [dispatch]);

  const handleOnPause = useCallback(() => {
    dispatch({ type: 'stop' });
  }, [dispatch]);

  useEffect(() => {
    if (parseInt(seconds, 10) >= parseInt(endSeconds, 10)) {
      setTimeout(() => {
        setShouldPlay(false);
        store.remove('ClipPlaying');
        dispatch({ type: 'setSeconds', seconds: 0 });
      }, 500);
    }
  }, [seconds, dispatch, endSeconds, setShouldPlay]);

  const [clipSoundtrackVolume, setClipSoundtrackVolume] = useState(0.05);

  const handleSubTitle = () => {
    if (reasonNotRenderVideo)
      return reasonNotRenderVideo
    else if (allowRendering)
      return 'Process your HD video to update it in your book and collage'
    else
      return 'HD Video updated in your collage & ready for Download!'
  };

  const renderClips = useMemo(() => {
    if (timelineClips && timelineClips.length > 0)
      return timelineClips.map((clip, index) => {
        const { volume, id, type, startAt, endAt } = clip;
        // Video clips are played by the single persistent element below; here we only
        // render image clips (per-clip mounting is fine for images — no autoplay lock).
        if (clip.video) return null;
        if (seconds >= startAt && seconds <= endAt) {
          return (
            <PreviewNode
              key={`preview-clip-${id}`}
              index={index}
              volume={volume <= 1 && typeof volume !== 'undefined' ? volume : 1}
              originalVolume={volume}
              currentTime={seconds}
              shouldPlay={shouldPlay}
              monthsFrameLayersData={monthsFrameLayersData}
              bookStyleMonth={bookStyleMonth?.attributes}
              onProgress={handleProgress}
              onPlay={handleOnPlay}
              onPause={handleOnPause}
              onEnded={() => { }}
              id={id}
              clipDetail={clip}
              type={type}
              startAt={startAt}
              endAt={endAt}
              // height={395}
              // width={width}
              dispatch={dispatch}
              isBuffering={isBuffering}
              setBuffering={(val) => setIsBuffering(val)}
              handlePauseClick={handlePauseClick}
              handlePlayClick={handlePlayClick}
              clipSoundtrackVolume={clipSoundtrackVolume}
              setClipSoundtrackVolume={setClipSoundtrackVolume}
            />
          );
        }
        return null;
      });
    return <RenderEmptyState />;
  }, [
    timelineClips,
    dispatch,
    handleOnPause,
    handleOnPlay,
    handleProgress,
    seconds,
    shouldPlay,
    width,
  ]);

  const onClose = () => {
    setIsProcessing(false)
  }

  return (
    <VideoPreviewContextProvider
      hasFadeTransition
      currentSeconds={seconds}
      clips={timelineClips}
    // isRunning={isRunning}
    >
      {(processVideo || isLoading) && <Modal
        maxWidth={500}
        shadeProps={{ pt: [0, 4] }}
        closeButton
        close={isLoading ? {} : { onClose }}
        childSx={{ marginTop: isLoading ? "20px" : '-40px', zIndex: 1 }}
        // aboveTooltip
        closeOnOutsideClick
      >
        {(isDataLoaded || isLoading || !bookStyleMonth?.attributes) ?
          <Flex justifyContent="center" alignItems="center" flexDirection="column" px={2}>
            <Text
              color="#000"
              fontFamily="Poppins"
              fontSize={"22px"}
              lineHeight="165%"
              mb={3}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                gap: '10px'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                <path d="M10.5 13.3327V9.99935M10.5 6.66602H10.5083M18.8333 9.99935C18.8333 14.6017 15.1023 18.3327 10.5 18.3327C5.89759 18.3327 2.16663 14.6017 2.16663 9.99935C2.16663 5.39698 5.89759 1.66602 10.5 1.66602C15.1023 1.66602 18.8333 5.39698 18.8333 9.99935Z" stroke="#1E1E1E" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
              </svg>  Processing in Progress
            </Text>
            <Text textAlign="center" fontFamily="Poppins" fontSize={16} fontWeight={500} letterSpacing="0.16px" lineHeight="165%" color="#64748B" >
              Your watch me grow video is currently processing. You will be emailed when your new video is ready!            </Text>
            <Image
              src={BabyBunnyWClock}
              maxWidth={300}
            />
          </Flex>
          : <Flex justifyContent="center" alignItems="center" flexDirection="column" px={2}>
            <Text
              color="#000"
              fontFamily="Poppins"
              fontSize={"22px"}
              lineHeight="165%"
              mb={3}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                gap: '10px'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                <path d="M10.5 13.3327V9.99935M10.5 6.66602H10.5083M18.8333 9.99935C18.8333 14.6017 15.1023 18.3327 10.5 18.3327C5.89759 18.3327 2.16663 14.6017 2.16663 9.99935C2.16663 5.39698 5.89759 1.66602 10.5 1.66602C15.1023 1.66602 18.8333 5.39698 18.8333 9.99935Z" stroke="#1E1E1E" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round" />
              </svg>  Video Processing
            </Text>
            <Text textAlign="center" fontFamily="Poppins" fontSize={16} fontWeight={500} letterSpacing="0.16px" lineHeight="165%" color="#64748B" >
              Processing usually takes 10-15 minutes. During this time you won’t be able to view or edit Watch Me Grow pages.
            </Text>
            <Image
              src={BabyBunnyWClock}
              maxWidth={300}
            />
            <Flex pb={2} sx={{ gap: "10px" }} flexDirection="column" alignItems="center" justifyContent="center">
              <Button sx={{ borderRadius: '23px', padding: '10px 20px', height: '44px' }} onClick={processWatchMeGrowVideo}>
                Continue with processing
              </Button>
              <Button onClick={onClose} sx={{ borderRadius: '23px', padding: '10px 20px', height: '44px', bg: '#BDBDBD', border: 0 }} o>
                Cancel
              </Button>
            </Flex>
          </Flex>}
      </Modal>}
      <Wrapper >
        {message && <Modal
          maxWidth={500}
          closeButton
          close={{ onClose: () => setMessage(null) }}
          childSx={{ marginTop: isLoading ? "20px" : '-40px', zIndex: 1 }}
          // aboveTooltip
          closeOnOutsideClick
        >
          {message}
        </Modal>}
        <Flex justifyContent={'center'} alignItems={'center'}>
          <Text fontFamily="Poppins" fontSize={22} letterSpacing="0.22px" lineHeight="165%">
            Video Preview
          </Text>
        </Flex>
        <Flex justifyContent="center" alignItems="center" flexDirection="column" my={2} >
          {allowRendering !== undefined && <Text color="#64748B" fontWeight={500} mb={2} fontFamily="Poppins" fontSize="16px" letterSpacing="0.16px" lineHeight="165%">
            {handleSubTitle()}
          </Text>}
          {allowRendering !== undefined && <>
            {isLoading ?
              <Loading local />
              : <>
                {(allowRendering) ?
                  <Button m="auto"
                    disabled={!!reasonNotRenderVideo}
                    color={"#9B59B6"} bg={"transparent"} height="44px" sx={{
                      borderRadius: '23px',
                      border: '1.5px solid #9B59B6'
                    }} p="0px 20px" onClick={() => allowRendering ? setIsProcessing(true) : setMessage(reasonNotRenderVideo)}>
                    Process Video
                  </Button>
                  : <a
                    href={`${video?.attributes?.['mp4-portrait']}?download=true`}
                    download
                  >
                    <Button m="auto" color={'white'} bg={'#9B59B6'} height="44px" sx={{
                      borderRadius: '23px',
                      border: 'unset'
                    }} p="0px 20px" >
                      Download
                    </Button>
                  </a>}
              </>
            }
          </>}
        </Flex>
        <Flex justifyContent="center" alignItems="center" flexDirection="column" maxWidth={600} m="auto" mb={[9, 0]}>
          <BufferingOverlay isBuffering={isBuffering} boxSx={{
            border: '2px dashed rgba(155, 89, 182, 0.10)',
            background: '#FDFAFF',
            borderRadius: '12.307px',
            overflow: 'hidden',
          }}>
            {/* Single persistent, user-activated video element (see handlePlayClick).
                Always mounted so its activation carries across clips; shown only while a
                video clip is on-screen, hidden (but kept alive) during image clips. */}
            {persistentVideoClip && (
              <div
                style={{
                  display: currentVideoClip ? 'grid' : 'none',
                  // backgroundColor: '#272929',
                  aspectRatio: '9 / 16',
                  gridArea: '1 / 1 / 2 / 2',
                  height: 'calc(100vh - 300px)',
                  maxWidth: 'calc(100vh * 9 / 16)',
                  overflowY: 'hidden',
                  margin: 'auto',
                }}
              >
                <VideoNode
                  key="wmg-persistent-video"
                  id="wmg-persistent-video"
                  index={persistentVideoClip.position}
                  clipDetail={persistentVideoClip}
                  currentTime={currentVideoClip ? seconds - persistentVideoClip.startAt : 0}
                  nodeInRange={!!currentVideoClip}
                  shouldPlay={shouldPlay}
                  onPlay={handleOnPlay}
                  onPause={handleOnPause}
                  onProgress={handleProgress}
                  volume={persistentVideoClip.volume <= 1 && typeof persistentVideoClip.volume !== 'undefined' ? persistentVideoClip.volume : 1}
                  originalVolume={persistentVideoClip.volume}
                  startAt={persistentVideoClip.startAt}
                  endAt={persistentVideoClip.endAt}
                  isBuffering={isBuffering}
                  setBuffering={(val) => setIsBuffering(val)}
                  isForEdit={false}
                />
              </div>
            )}
            {renderClips}
          </BufferingOverlay >
          <PreviewControls
            isPlaying={shouldPlay}
            onPlayClick={handlePlayClick}
            onPauseClick={handlePauseClick}
            currentSeconds={seconds}
            endSeconds={endSeconds}
            onSliderChange={handleSliderChange}
            isBuffering={isBuffering}
          />
        </Flex>
      </Wrapper>
    </VideoPreviewContextProvider >
  );
};

export default WatchMeGrowVideoPreview;